import { graphql } from 'react-relay';
import React, { memo, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Flex } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { Browser } from '@atlaskit/media-picker';
import type { UploadRejectionData } from '@atlaskit/media-picker';
import type { UploadEndEventPayload } from '@atlaskit/media-picker/types';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import {
	COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
	COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
	COVER_PICTURE_REMOVE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getAttachmentSizeLimitErrorFlag } from '@confluence/fabric-media-support/entry-points/attachmentSizeLimitErrorHelpers.ts';
import { useMediaSSRModeAndAuthConfig } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';
import {
	createEditorMediaProvider,
	createRendererMediaProvider,
} from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import type { COVERPICTUREWIDTH } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';

import type { GenerateImageProps } from '@atlassian/ai-image-generation';

import {
	getRendererCoverPictureInfo,
	propertyToId,
	isAllowedExternalCoverPictureUrl,
} from '../coverPictureHelperFunctions.ts';
import type {
	CoverPictureId,
	EditorPageCoverPictureComponentProps,
} from '../pageCoverPictureTypes.ts';
import { CoverPictureIdPropertyKey } from '../pageCoverPictureTypes.ts';
import {
	DEFAULT_POSITION_VALUE,
	SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH,
} from '../pageCoverPictureConstants.ts';
import CoverPictureQuery, {
	type CoverPictureQuery$data,
	type CoverPictureQuery$variables,
} from '../__generated__/CoverPictureQuery.graphql.ts';

import type {
	GraphQLCoverPictureWidth,
	EditorPageCoverPictureUpdateCoverPictureWidthMutation as EditorPageCoverPictureUpdateCoverPictureWidthMutation$data,
	EditorPageCoverPictureUpdateCoverPictureWidthMutationVariables as EditorPageCoverPictureUpdateCoverPictureWidthMutation$variables,
} from './__types__/EditorPageCoverPictureUpdateCoverPictureWidthMutation.ts';
// eslint-disable-next-line import/no-cycle
import { MediaCoverPicture } from './MediaCoverPicture.tsx';
import { ImagePicker } from './ImagePicker.tsx';
import { UnsplashCoverPicture } from './UnsplashCoverPicture.tsx';
import { CoverPictureWrapper } from './EditorPageCoverPictureWrapper.tsx';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const i18n = defineMessages({
	errorFlagTitle: {
		id: 'page-cover-picture.error.flag.title',
		description: 'Title within the error flag that appears if some upload image error occurred.',
		defaultMessage: 'We can’t upload your header image',
	},
	errorFlagDescription: {
		id: 'page-cover-picture.error.flag.description',
		description:
			'Description within the error flag that appears if some upload image error occurred.',
		defaultMessage: 'Refresh and try again.',
	},
	suggestHeaderNextTime: {
		id: `page-cover-picture.ai-suggested-image-header.flag.suggest-next-time`,
		description: 'Ask user if they would like us to suggest a header image next time',
		defaultMessage: 'Try a suggested header next time?',
	},
	suggestHeaderKeepSuggestions: {
		id: `page-cover-picture.ai-suggested-image-header.flag.suggest-next-time.accept`,
		// eslint-disable-next-line @atlassian/i18n/require-description
		description:
			'The text is shown on a confirmation button in a flag that asks the user whether to continue receiving AI-suggested header images for pages. When clicked, it keeps the AI suggestions enabled.',
		defaultMessage: 'Yes, keep suggestions',
	},
	suggestHeaderTurnOffSuggestions: {
		id: `page-cover-picture.ai-suggested-image-header.flag.suggest-next-time.decline`,
		description:
			'The text is shown on a decline button in a flag that asks the user whether to continue receiving AI-suggested header images for pages. When clicked, it disables the AI header image suggestions.',
		defaultMessage: 'No, turn off',
	},
	aiGenerationErrorFlagTitle: {
		id: 'page-cover-picture.ai-generation.error.flag.title',
		description: 'Title within the error flag when AI header image generation fails.',
		defaultMessage: "We couldn't generate your header image",
	},
	aiGenerationErrorFlagDescription: {
		id: 'page-cover-picture.ai-generation.error.flag.description',
		description: 'Description within the error flag when AI header image generation fails.',
		defaultMessage: 'Try again or choose a different image.',
	},
	aiGenerationErrorFlagDescriptionWithReason: {
		id: 'page-cover-picture.ai-generation.error.flag.description-with-reason',
		description:
			'Description within the error flag when AI header image generation fails with a specific error reason from the API.',
		defaultMessage: '{errorReason}. Try again or choose a different image.',
	},
});

const EditorPageCoverPictureComponent = memo(
	({
		contentId,
		isPublishingContent,
		contentType,
		userOffline,
		hasCoverPicture,
		contentPropertyKey,
		isSmartSpaceOverview,
		coverPictureState,
		isUnpublishedDraft,
		flags,
		floatingToolbarButtonStyle,
		width,
		dynamicCoverImageHeight,
		isReadOnly,
		spaceKey,
		aiSuggestedUnsplashImages,
		isFixedWidthImageOption,
		aiHeaderImageStatus,
		aiHeaderImageFileId,
		aiHeaderImageError,
		generateAIHeaderImage,
		setAIHeaderImageStatus,
		isSmartCreate,
	}: EditorPageCoverPictureComponentProps & WithFlagsProps) => {
		const [isMediaBrowserOpen, setIsMediaBrowserOpen] = useState<boolean>(false);
		const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);
		const [isUploadingPicture, setIsUploadingPicture] = useState<boolean>(false);
		const [isRegeneratingImage, setIsRegeneratingImage] = useState<boolean>(false);
		const [regenerationCount, setRegenerationCount] = useState<number>(0);
		// mediaConfig does not use onUploadRejection when in read only (RendererMediaProvider), but does need it otherwise (EditorMediaProvider).
		const [mediaConfig, setMediaConfig] = useState<{
			collection: string | undefined;
			authProvider: MediaClientConfig['authProvider'];
			onUploadRejection?: (data: UploadRejectionData) => boolean;
			useSha256ForUploads?: boolean;
		} | null>(null);
		// Using ref since onHandleUploadEnd event uses previous cover picture information and upload will fail
		// since the information is from the past render
		const coverPicture = useRef<{
			draft: CoverPictureId | null;
		}>({ draft: null });
		// need this force update since we are using useRef for the cover picture and therefore won't update the image automatically on change
		const [, forceUpdate] = useState<any>();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const isLivePage = useIsCurrentPageLive();
		// need this state since we want to show reposition option right after we upload a photo
		const [shouldShowReposition, setShouldShowReposition] = useState<boolean>(false);
		const [shouldShowAIUnsplashHeaderToolbar, setShouldShowAIUnsplashHeaderToolbar] =
			useState<boolean>(false);

		// AI Image Acceptance Flow state
		const [isRetryingAIImage, setIsRetryingAIImage] = useState<boolean>(false);
		// Track previous status to detect transition from LOADING_WITH_ACCEPTANCE_FLOW
		const prevAIHeaderImageStatusRef = useRef<string | undefined>(undefined);
		// Track previous status to detect transition INTO ERROR state (for showing error flag)
		const prevStatusForErrorFlagRef = useRef<string | null>(aiHeaderImageStatus ?? null);

		const ssrConfig = useMediaSSRModeAndAuthConfig({ contentId, isExternalShare });
		const isServerPlaceholder = useRenderServerPlaceholder(true);
		const hasSSRConfigInSSRModeOrHydration =
			(isServerPlaceholder || window.__SSR_RENDERED__) && ssrConfig;

		const experienceTracker = useContext(ExperienceTrackerContext);
		const { formatMessage } = useIntl();
		const intl = useIntl();
		const [currentAIUnsplashImageIndex, setCurrentAIUnsplashImageIndex] = useState<number>(0);
		const smartSpaceOverviewHeight = isSmartSpaceOverview
			? SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH
			: undefined;
		const analyticsSource = isSmartSpaceOverview ? 'smartSpaceOverview' : 'editorPageCoverPicture';

		const { updateContentProperty } = useUpdateContentProperty({
			contentId,
			contentType: contentType || 'page',
			options: {
				refetchQueries: () => [
					{
						query: CoverPictureQuery,
						variables: {
							contentId,
							status: ['draft'],
							draftShareId: getDraftShareIdFromQueryParam(),
						},
					},
				],
			},
		});
		const { data, refetch } = useQueryInterop<CoverPictureQuery$data, CoverPictureQuery$variables>(
			CoverPictureQuery,
			{
				context: { single: true },
				variables: {
					contentId,
					status: ['current', 'draft'],
					draftShareId: getDraftShareIdFromQueryParam(),
				},
			},
		);

		const coverPictureData = getRendererCoverPictureInfo(data, isLivePage);
		const coverPictureDataPosition = coverPictureData?.coverPicture?.published?.value?.position;
		const coverPictureDataId = coverPictureData?.coverPicture?.published?.value?.id;

		// Need this since the isUnsplash prop isn't getting the up to date values
		// Check if the cover picture is from Unsplash or Atlassian hosted CDN (dam-cdn or wac-cdn)
		const coverPictureId = coverPicture.current?.draft?.value?.id;

		// Setting this to null so that createLiveDocHeaderImageAnalyticsEvent is not fired until
		// isUnsplashLocalCheck is set to either true/false
		const isUnsplashLocalCheck =
			coverPictureId === undefined ? null : isAllowedExternalCoverPictureUrl(coverPictureId);

		const isGeneratingHeaderImage =
			aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW';
		const [mutateCoverPictureWidth] = useMutationInterop<
			EditorPageCoverPictureUpdateCoverPictureWidthMutation$data,
			EditorPageCoverPictureUpdateCoverPictureWidthMutation$variables
		>(
			graphql`
				mutation EditorPageCoverPictureUpdateCoverPictureWidthMutation(
					$contentId: ID!
					$coverPictureWidth: GraphQLCoverPictureWidth!
				) {
					updateCoverPictureWidth(
						input: { contentId: $contentId, coverPictureWidth: $coverPictureWidth }
					) {
						content {
							id
						}
					}
				}
			`,
			{
				onError: (error) => {
					if (isUnauthorizedError(error)) {
						markErrorAsHandled(error);
					}
					getMonitoringClient().submitError(error, {
						attribution: Attribution.MIGRATIONS_TOOLING,
					});
				},
			},
		);

		const onCoverPictureUpdatedSubscriptionData = useCallback(
			async (_: string, response: any) => {
				try {
					await refetch();
					const { coverPictureUpdated } = response || {};
					const value = JSON.parse(coverPictureUpdated?.value);
					const version = coverPictureUpdated?.version;
					coverPicture.current['draft'] = { value, version };
					void coverPictureState.setHasCoverPicture(true);
					setIsUploadingPicture(false);
					void coverPictureState.setIsUploadingPicture(false);
					void coverPictureState.setCoverPictureId(value.id);
					setIsRegeneratingImage(false);
					forceUpdate(Math.random());
				} catch (error) {
					getMonitoringClient().submitError(error, {
						attribution: Attribution.PAGE_EXPERIENCES,
					});
					markErrorAsHandled(error);
				}
			},
			[refetch, coverPictureState],
		);

		const onCoverPictureWidthUpdatedSubscription = useCallback(
			(_: string, response: any) => {
				const { coverPictureWidthUpdated } = response || {};

				const value = coverPictureWidthUpdated?.coverPictureWidth;
				void coverPictureState.toggleCoverPictureWidthButton(value);
				forceUpdate(Math.random());
			},
			[coverPictureState],
		);

		const onCoverPictureDeletedSubscription = useCallback(async () => {
			try {
				await refetch();
				coverPicture.current['draft'] = null;
				void coverPictureState.setHasCoverPicture(false);
				void coverPictureState.setCoverPictureId(null);
				void coverPictureState.setIsModalOpen(false);
				void coverPictureState.setIsAddHeaderImageButtonFocused(false);
				forceUpdate(Math.random());
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		}, [refetch, coverPictureState]);

		// Smart Space Overview: subscriptions don't fire, so refetch after mutations
		const refetchAfterMutation = useCallback(async () => {
			try {
				await refetch();
				forceUpdate(Math.random());
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		}, [refetch]);

		const subscriptionResourceType = contentType || 'page';

		useSubscriptionEvent({
			subscriberId: 'EditingCoverPictureSubscription',
			contentId,
			delta: 'coverPictureUpdated',
			contentType: subscriptionResourceType,
			onDataReceived: onCoverPictureUpdatedSubscriptionData,
		});

		useSubscriptionEvent({
			subscriberId: 'EditorCoverPictureDeletedSubscription',
			contentId,
			delta: 'coverPictureDeleted',
			contentType: subscriptionResourceType,
			onDataReceived: onCoverPictureDeletedSubscription,
		});

		useSubscriptionEvent({
			subscriberId: 'EditorCoverPictureWidthUpdatedSubscription',
			contentId,
			delta: 'coverPictureWidthUpdated',
			contentType: subscriptionResourceType,
			onDataReceived: onCoverPictureWidthUpdatedSubscription,
		});

		const propertyKeyForMutations = contentPropertyKey ?? CoverPictureIdPropertyKey.DRAFT;

		// Check for existing cover picture
		useEffect(() => {
			const properties = data?.content?.nodes?.[0]?.properties?.nodes || [];
			const draft = properties.find((p) => p?.key === CoverPictureIdPropertyKey.DRAFT);
			const published = properties.find((p) => p?.key === CoverPictureIdPropertyKey.PUBLISHED);
			// Smart Space Overview: when mutating PUBLISHED (no draft), display from published
			const propertyToUse =
				contentPropertyKey === CoverPictureIdPropertyKey.PUBLISHED ? published : draft;
			coverPicture.current = {
				draft: propertyToId(propertyToUse),
			};
			forceUpdate(Math.random());
		}, [data, contentPropertyKey]);

		// Setup media config
		useEffect(() => {
			const showAttachmentLimitReachedFlag = (rejectionData: UploadRejectionData) => {
				if (flags && rejectionData) {
					void flags
						.showErrorFlag(getAttachmentSizeLimitErrorFlag(rejectionData, intl))
						.then(() => {
							setIsUploadingPicture(false);
							void coverPictureState.setIsUploadingPicture(false);

							experienceTracker.abort({
								name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
								reason: 'Cover picture media upload too large',
							});

							createAnalyticsEvent({
								type: 'sendScreenEvent',
								data: {
									name: 'pageCoverPictureAttachmentTooLargeFlag',
								},
							}).fire();
						});
				}
				return !!rejectionData;
			};

			// If read only, use renderer media provider, as we only need the auth provider from rendererMediaProvider, and user without edit perms (read only) doesn't have access to editorMediaProvider APIs
			// If not read only, use editor media provider
			const createMediaConfig = async () => {
				// Media provider is not supported in SSR, skip it for now.
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					return;
				}
				if (isReadOnly) {
					const { viewMediaClientConfig } = await createRendererMediaProvider({
						contentId,
						isExternalShare: false,
					});
					if (!viewMediaClientConfig) {
						return null;
					}
					setMediaConfig({
						collection: `contentId-${contentId}`,
						authProvider: viewMediaClientConfig.authProvider,
					});
				} else {
					const { uploadParams, uploadMediaClientConfig } = await createEditorMediaProvider({
						contentId,
						contentType: 'page',
						isExternalShare,
						onUploadRejection: flags ? showAttachmentLimitReachedFlag : undefined,
					});

					if (uploadMediaClientConfig == undefined) {
						return null;
					}
					setMediaConfig({
						collection: uploadParams?.collection,
						authProvider: uploadMediaClientConfig.authProvider,
						onUploadRejection: uploadParams?.onUploadRejection,
						useSha256ForUploads: uploadMediaClientConfig?.useSha256ForUploads,
					});
				}
			};

			void createMediaConfig();
		}, [
			contentId,
			isExternalShare,
			flags,
			intl,
			coverPictureState,
			createAnalyticsEvent,
			experienceTracker,
			isReadOnly,
		]);

		// handling user publishes - we only need to worry about firing the analytic event as backend will take care of the
		// published cover picture content property mutation due to scheduled publishes
		useEffect(() => {
			// fire analytic if we're publishing a page with a cover image
			if (isPublishingContent && !!coverPicture.current?.draft?.value.id) {
				if (isUnpublishedDraft) {
					if (coverPictureState.state.isAIUnsplashImage) {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'published',
								actionSubject: 'aiUnsplashImage',
								actionSubjectId: 'aiSuggestedUnsplashImage',
								source: analyticsSource,
								objectId: contentId,
							},
						}).fire();
					} else {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'createdWithHeaderImage',
								actionSubject: 'page',
								objectId: contentId,
								source: analyticsSource,
							},
						}).fire();
					}
				} else {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'updatedWithHeaderImage',
							actionSubject: 'page',
							objectId: contentId,
							source: analyticsSource,
						},
					}).fire();
				}
			}
		}, [
			contentId,
			isPublishingContent,
			isUnpublishedDraft,
			createAnalyticsEvent,
			coverPictureState.state.isAIUnsplashImage,
			analyticsSource,
		]);

		useEffect(() => {
			if (isPublishingContent && hasCoverPicture) {
				void coverPictureState.setShouldShowReposition(false);
			}
		}, [hasCoverPicture, isPublishingContent, coverPictureState]);

		const showErrorFlag = () => {
			void flags.showFlag({
				type: 'error',
				title: formatMessage(i18n.errorFlagTitle),
				description: formatMessage(i18n.errorFlagDescription),
				messageType: 'transactional',
				messageId: 'page-cover-picture-error-flag',
			});
			setIsUploadingPicture(false);
			void coverPictureState.setIsUploadingPicture(false);

			experienceTracker.stopOnError({
				name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
				error: new Error('Error with cover picture media upload'),
			});

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'pageCoverPictureErrorFlag',
				},
			}).fire();
		};

		const showAIGenerationErrorFlag = useCallback(
			(errorReason?: string | null) => {
				// Use the API error reason if available, otherwise fall back to generic message
				const description = errorReason
					? formatMessage(i18n.aiGenerationErrorFlagDescriptionWithReason, { errorReason })
					: formatMessage(i18n.aiGenerationErrorFlagDescription);

				void flags.showFlag({
					type: 'error',
					title: formatMessage(i18n.aiGenerationErrorFlagTitle),
					description,
					messageType: 'transactional',
					messageId: 'page-cover-picture-ai-generation-error-flag',
				});

				// Clear loading states
				setIsUploadingPicture(false);
				void coverPictureState.setIsUploadingPicture(false);
				setIsRetryingAIImage(false);

				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'pageCoverPictureAIGenerationErrorFlag',
						attributes: {
							errorReason: errorReason || 'unknown',
						},
					},
				}).fire();
			},
			[flags, formatMessage, coverPictureState, createAnalyticsEvent],
		);

		const handleUploadStart = () => {
			setIsUploadingPicture(true);
			void coverPictureState.setIsUploadingPicture(true);
			void coverPictureState.setIsChangingPicture(false);
			experienceTracker.start({
				name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
			});
		};

		const handleUploadEnd = (event: UploadEndEventPayload) => {
			const fileType = event.file.type;
			const id = event?.file?.id;
			if (!fileType.includes('image')) {
				showErrorFlag();
			} else if (id) {
				const currentAIFCAutoImage = coverPictureState.state.isCurrentAIFCAutoImage;
				// must JSON stringify propertyValue object since mutation expects a string for this parameter
				const propertyValue = JSON.stringify({
					id,
					position: DEFAULT_POSITION_VALUE,
				});

				const updatePromise = updateContentProperty({
					propertyKey: propertyKeyForMutations,
					propertyValue,
					isDelete: false,
					hasExistingProperty: hasCoverPicture,
				});

				if (isSmartSpaceOverview && updatePromise) {
					void updatePromise.then(() => {
						void refetchAfterMutation();
						setIsUploadingPicture(false);
						void coverPictureState.setIsUploadingPicture(false);
					});
				}

				setShouldShowReposition(true);
				void coverPictureState.setShouldShowReposition(true);
				void coverPictureState.setIsCurrentAIFCAutoImage(false);
				experienceTracker.succeed({
					name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: analyticsSource,
						attributes: {
							imageSource: 'uploaded',
							imageId: id,
							inputMethod: 'header',
							isLivePage,
							isAIGenerationEnabled: isExperimentEnabled(
								'cc_page_experiences_ai_header_image_generation',
							),
							isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
								'cc_page_experiences_aifc_auto_generated_header_img',
							),
							isAIFCAutoGeneratedImage: false,
						},
					},
				}).fire();
				if (currentAIFCAutoImage) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'replaced',
							actionSubject: 'image',
							actionSubjectId: 'autoGeneratedAIFCImageReplaced',
							source: analyticsSource,
							attributes: {
								imageSource: 'uploaded',
								inputMethod: 'header',
								isAIGenerationEnabled: isExperimentEnabled(
									'cc_page_experiences_ai_header_image_generation',
								),
							},
						},
					}).fire();
				}
			} else {
				showErrorFlag();
			}
		};

		const handleUnsplashUpload = useCallback(
			(photoURL, triggeredFromRegenerate = false) => {
				const currentAIFCAutoImage = coverPictureState.state.isCurrentAIFCAutoImage;
				if (!triggeredFromRegenerate) {
					setIsUploadingPicture(true);
					void coverPictureState.setIsUploadingPicture(true);
				}
				const propertyValue = JSON.stringify({
					id: photoURL,
					position: DEFAULT_POSITION_VALUE,
				});
				const updatePromise = updateContentProperty({
					propertyKey: propertyKeyForMutations,
					propertyValue,
					isDelete: false,
					hasExistingProperty: hasCoverPicture,
				});
				if (isSmartSpaceOverview && updatePromise) {
					void updatePromise.then(() => {
						void refetchAfterMutation();
						if (!triggeredFromRegenerate) {
							setIsUploadingPicture(false);
							void coverPictureState.setIsUploadingPicture(false);
						}
					});
				}
				if (coverPictureState.state.isAIUnsplashImage) {
					setShouldShowReposition(false);
					setShouldShowAIUnsplashHeaderToolbar(true);
					void coverPictureState.setShouldShowAIUnsplashHeaderToolbar(true);
				} else {
					setShouldShowReposition(true);
					void coverPictureState.setShouldShowReposition(true);
				}
				void coverPictureState.setIsCurrentAIFCAutoImage(false);
				experienceTracker.succeed({
					name: COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: analyticsSource,
						attributes: {
							imageSource: 'unsplash',
							imageId: photoURL,
							inputMethod: 'header',
							isLivePage,
							isAIGenerationEnabled: isExperimentEnabled(
								'cc_page_experiences_ai_header_image_generation',
							),
							isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
								'cc_page_experiences_aifc_auto_generated_header_img',
							),
							isAIFCAutoGeneratedImage: false,
						},
					},
				}).fire();
				if (currentAIFCAutoImage) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'replaced',
							actionSubject: 'image',
							actionSubjectId: 'autoGeneratedAIFCImageReplaced',
							source: analyticsSource,
							attributes: {
								imageSource: 'unsplash',
								inputMethod: 'header',
								isAIGenerationEnabled: isExperimentEnabled(
									'cc_page_experiences_ai_header_image_generation',
								),
							},
						},
					}).fire();
				}
			},
			[
				coverPictureState,
				createAnalyticsEvent,
				experienceTracker,
				isLivePage,
				updateContentProperty,
				hasCoverPicture,
				propertyKeyForMutations,
				isSmartSpaceOverview,
				refetchAfterMutation,
				analyticsSource,
			],
		);

		const handleAIGeneratedUpload = useCallback(
			(mediaFileId: string, skipStatusUpdate = false, isAIFCAutoGeneratedImage = false) => {
				const propertyValue = JSON.stringify({
					id: mediaFileId,
					position: DEFAULT_POSITION_VALUE,
				});
				const updatePromise = updateContentProperty({
					propertyKey: propertyKeyForMutations,
					propertyValue,
					isDelete: false,
					hasExistingProperty: hasCoverPicture,
				});
				if (isSmartSpaceOverview && updatePromise) {
					void updatePromise.then(() => {
						void refetchAfterMutation();
					});
				}
				// Don't set SUCCESSFULLY_SET if we're showing acceptance flow
				// The status will be set when user accepts the image
				if (!skipStatusUpdate) {
					setAIHeaderImageStatus?.('SUCCESSFULLY_SET');
				}
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: analyticsSource,
						attributes: {
							imageSource: 'aiGenerated',
							imageId: mediaFileId,
							inputMethod: 'header',
							isLivePage,
							isAIGenerationEnabled: isExperimentEnabled(
								'cc_page_experiences_ai_header_image_generation',
							),
							isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
								'cc_page_experiences_aifc_auto_generated_header_img',
							),
							isAIFCAutoGeneratedImage,
						},
					},
				}).fire();
			},
			[
				createAnalyticsEvent,
				isLivePage,
				setAIHeaderImageStatus,
				updateContentProperty,
				hasCoverPicture,
				propertyKeyForMutations,
				isSmartSpaceOverview,
				refetchAfterMutation,
				analyticsSource,
			],
		);

		const updateCoverPictureState = useCallback(async () => {
			await coverPictureState.setHasCoverPicture(true);
		}, [coverPictureState]);

		useEffect(() => {
			if (
				aiSuggestedUnsplashImages?.[0] &&
				!isUploadingPicture &&
				!coverPictureState.state.hasCoverPicture
			) {
				try {
					handleUnsplashUpload(
						aiSuggestedUnsplashImages?.[currentAIUnsplashImageIndex]?.fullSizeImageUrl,
					);
				} finally {
					void updateCoverPictureState();
				}
			}
		}, [
			currentAIUnsplashImageIndex,
			aiSuggestedUnsplashImages,
			isUploadingPicture,
			handleUnsplashUpload,
			coverPictureState.state.hasCoverPicture,
			coverPictureState.setHasCoverPicture,
			updateCoverPictureState,
		]);

		useEffect(() => {
			if (
				!isExperimentEnabled('cc_page_experiences_ai_header_image_generation') &&
				!isExperimentEnabled('cc_page_experiences_aifc_auto_generated_header_img')
			) {
				return;
			}

			// Track status transitions to detect when loading completes
			const prevStatus = prevAIHeaderImageStatusRef.current;
			const wasLoadingWithAcceptance = prevStatus === 'LOADING_WITH_ACCEPTANCE_FLOW';
			prevAIHeaderImageStatusRef.current = aiHeaderImageStatus;

			if (isGeneratingHeaderImage) {
				void coverPictureState.setIsUploadingPicture(true);
			} else {
				void coverPictureState.setIsUploadingPicture(false);
				if (aiHeaderImageFileId && aiHeaderImageStatus !== 'SUCCESSFULLY_SET') {
					// Check if we should show acceptance flow:
					// Either from the stored params (when wrapped function is called) or from status transition
					const lastParams = coverPictureState.state
						.lastAIGenerationParams as GenerateImageProps | null;
					const shouldShowAcceptance = lastParams?.showAcceptanceFlow || wasLoadingWithAcceptance;

					const isAIFCAutoGenerated = !shouldShowAcceptance && !!isSmartCreate;
					void coverPictureState.setIsCurrentAIFCAutoImage(isAIFCAutoGenerated);

					try {
						// If showing acceptance flow, don't set status to SUCCESSFULLY_SET yet
						// User needs to accept first
						// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
						handleAIGeneratedUpload(aiHeaderImageFileId, shouldShowAcceptance, isAIFCAutoGenerated);
					} finally {
						void updateCoverPictureState();
					}

					// Show the acceptance flow with repositioning when image is ready
					if (shouldShowAcceptance) {
						// Clear uploading state to allow acceptance UI to show
						// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
						setIsUploadingPicture(false);
						void coverPictureState.setShouldShowAIImageAcceptanceFlow(true);
						// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
						setIsRetryingAIImage(false);
					}
				}
			}
		}, [
			isGeneratingHeaderImage,
			coverPictureState,
			aiHeaderImageFileId,
			handleAIGeneratedUpload,
			updateCoverPictureState,
			aiHeaderImageStatus,
			isSmartCreate,
		]);

		// Watch for AI generation error status and show error flag to user
		// Only show flag on TRANSITION into ERROR state, not on re-renders while in ERROR state
		useEffect(() => {
			const prevStatus = prevStatusForErrorFlagRef.current;
			const currentStatus = aiHeaderImageStatus;

			// Only show error flag when TRANSITIONING into ERROR state
			// This prevents duplicate flags from re-renders while already in ERROR state
			const isTransitioningToError = prevStatus !== 'ERROR' && currentStatus === 'ERROR';

			// Don't show error flags for AIFC auto-generated images (where the image is
			// generated automatically from the title without user interaction).
			// The auto flow uses 'LOADING' status (no acceptance flow), while user-initiated
			// generation uses 'LOADING_WITH_ACCEPTANCE_FLOW'. We detect auto flow by checking
			// if the previous status was 'LOADING' (not 'LOADING_WITH_ACCEPTANCE_FLOW').
			const wasAIFCAutoGenerated = prevStatus === 'LOADING';

			if (isTransitioningToError && !wasAIFCAutoGenerated) {
				// Due to a false positive
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
				showAIGenerationErrorFlag(aiHeaderImageError);
			}

			// Update the ref for next render
			prevStatusForErrorFlagRef.current = currentStatus ?? null;
		}, [aiHeaderImageStatus, aiHeaderImageError, showAIGenerationErrorFlag]);

		const onAcceptAIUnsplashImage = useCallback(async () => {
			setShouldShowReposition(true);
			setShouldShowAIUnsplashHeaderToolbar(false);
			void coverPictureState.setShouldShowAIUnsplashHeaderToolbar(false);
			void coverPictureState.setShouldShowReposition(true);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'accepted',
					actionSubject: 'aiUnsplashImage',
					actionSubjectId: 'aiSuggestedUnsplashImage',
					source: analyticsSource,
					attributes: {
						regenerationCount,
					},
				},
			}).fire();
		}, [coverPictureState, regenerationCount, createAnalyticsEvent, analyticsSource]);

		// Handler for accepting AI generated image (from AIImageAcceptanceRepositioner)
		const onAcceptAIGeneratedImage = useCallback(() => {
			void coverPictureState.setShouldShowAIImageAcceptanceFlow(false);
			setAIHeaderImageStatus?.('SUCCESSFULLY_SET');
			void coverPictureState.setLastAIGenerationParams(null);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'accepted',
					actionSubject: 'aiGeneratedImage',
					actionSubjectId: 'aiGeneratedHeaderImage',
					source: analyticsSource,
					attributes: {
						regenerationCount,
					},
				},
			}).fire();
		}, [
			setAIHeaderImageStatus,
			regenerationCount,
			createAnalyticsEvent,
			coverPictureState,
			analyticsSource,
		]);

		// Handler for retrying AI image generation (from AIImageAcceptanceRepositioner)
		const onRetryAIGeneratedImage = useCallback(async () => {
			const lastParams = coverPictureState.state
				.lastAIGenerationParams as GenerateImageProps | null;

			if (!generateAIHeaderImage) {
				// eslint-disable-next-line no-console
				console.warn('[AI Header Image] generateAIHeaderImage is not available');
				return;
			}

			if (!lastParams) {
				// This should never happen - if the user clicked retry, there should be stored params
				// eslint-disable-next-line no-console
				console.error('[AI Header Image] Cannot retry: no stored generation parameters found');
				return;
			}

			setIsRetryingAIImage(true);
			setRegenerationCount((prev) => prev + 1);
			await generateAIHeaderImage({
				...lastParams,
				showAcceptanceFlow: true,
			});
		}, [generateAIHeaderImage, coverPictureState.state.lastAIGenerationParams]);

		// Wrapped generateAIHeaderImage that stores params for retry functionality
		const wrappedGenerateAIHeaderImage = useCallback(
			async (props: GenerateImageProps) => {
				if (generateAIHeaderImage) {
					// Store params in container state so we can retry with the same params
					// Using container state ensures params survive component re-mounts
					void coverPictureState.setLastAIGenerationParams(props);
					return generateAIHeaderImage(props);
				}
				return undefined;
			},
			[generateAIHeaderImage, coverPictureState],
		);

		const onRegenerate = useCallback(async () => {
			if (aiSuggestedUnsplashImages && aiSuggestedUnsplashImages.length > 0) {
				// This is used for to tell the toolbar that the image is regenerating
				setIsRegeneratingImage(true);
				// This is used for analytics to track if the user has regenerated and if they ultimately accepted or declined
				setRegenerationCount(regenerationCount + 1);
				// This will loop through the images in the array and circle back to the first image if the last image is reached
				// The images get less and less relevant as the index increases so it's better to start from the beginning
				// Than it is to get the next 30 results from the API
				const nextIndex = (currentAIUnsplashImageIndex + 1) % aiSuggestedUnsplashImages.length;
				setCurrentAIUnsplashImageIndex(nextIndex);
				handleUnsplashUpload(aiSuggestedUnsplashImages?.[nextIndex]?.fullSizeImageUrl, true);
			}
		}, [
			currentAIUnsplashImageIndex,
			aiSuggestedUnsplashImages,
			handleUnsplashUpload,
			setIsRegeneratingImage,
			setRegenerationCount,
			regenerationCount,
		]);

		const sendAIUnsplashOptInAnalytics = useCallback(
			(optIn: boolean) => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'optIn',
						actionSubject: 'aiUnsplashImage',
						actionSubjectId: 'aiSuggestedUnsplashImage',
						source: analyticsSource,
						attributes: {
							optIn,
						},
					},
				}).fire();
			},
			[createAnalyticsEvent, analyticsSource],
		);

		const showAIUnsplashHeaderRemovedFlag = useCallback(() => {
			if (localStorage.getItem(localStorageKeys.AI_UNSPLASH_HEADER_ACCEPTED) !== null) {
				return;
			}

			void flags.showFlag({
				title: formatMessage(i18n.suggestHeaderNextTime),
				type: 'info',
				id: 'ai-suggested-image-header-removed',
				actions: [
					{
						content: formatMessage(i18n.suggestHeaderKeepSuggestions),
						onClick: () => {
							localStorage.setItem(localStorageKeys.AI_UNSPLASH_HEADER_ACCEPTED, 'true');
							sendAIUnsplashOptInAnalytics(true);
							void flags.hideFlag('ai-suggested-image-header-removed');
						},
					},
					{
						content: formatMessage(i18n.suggestHeaderTurnOffSuggestions),
						onClick: () => {
							localStorage.setItem(localStorageKeys.AI_UNSPLASH_HEADER_ACCEPTED, 'false');
							sendAIUnsplashOptInAnalytics(false);
							void flags.hideFlag('ai-suggested-image-header-removed');
						},
					},
				],
			});

			setTimeout(() => {
				void flags.hideFlag('ai-suggested-image-header-removed');
			}, 8000);
		}, [flags, formatMessage, sendAIUnsplashOptInAnalytics]);

		const onRemoveImage = useCallback(async () => {
			void coverPictureState.setIsRemovingPicture(true);
			void coverPictureState.setIsAddHeaderImageButtonFocused(false);
			const updatePromise = updateContentProperty({
				propertyKey: propertyKeyForMutations,
				isDelete: true,
				hasExistingProperty: hasCoverPicture,
			});
			if (isSmartSpaceOverview && updatePromise) {
				void updatePromise.then(async () => {
					if (typeof refetch === 'function') {
						await refetch();
					}
					coverPicture.current['draft'] = null;
					void coverPictureState.setHasCoverPicture(false);
					void coverPictureState.setCoverPictureId(null);
					void coverPictureState.setIsModalOpen(false);
					forceUpdate(Math.random());
				});
			}
			void coverPictureState.setIsChangingPicture(false);
			void coverPictureState.setIsModalOpen(false);
			if (coverPictureState.state.isAIUnsplashImage) {
				void coverPictureState.setIsAIUnsplashImage(false);
				void coverPictureState.setAISuggestedUnsplashImages(undefined);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'removed',
						actionSubject: 'aiUnsplashImage',
						actionSubjectId: 'aiSuggestedUnsplashImage',
						source: analyticsSource,
						attributes: {
							regenerationCount,
						},
					},
				}).fire();
				showAIUnsplashHeaderRemovedFlag();
			}
			// Clear AI image acceptance flow state when image is removed/discarded
			if (coverPictureState.state.shouldShowAIImageAcceptanceFlow) {
				void coverPictureState.setShouldShowAIImageAcceptanceFlow(false);
				void coverPictureState.setLastAIGenerationParams(null);
				// Set to SUCCESSFULLY_SET
				setAIHeaderImageStatus?.('SUCCESSFULLY_SET');
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'discarded',
						actionSubject: 'aiGeneratedImage',
						actionSubjectId: 'aiGeneratedHeaderImage',
						source: analyticsSource,
						attributes: {
							regenerationCount,
						},
					},
				}).fire();
			}
			void coverPictureState.setIsCurrentAIFCAutoImage(false);
			void coverPictureState.setIsRemovingPicture(false);
			experienceTracker.succeed({
				name: COVER_PICTURE_REMOVE_EXPERIENCE,
			});
		}, [
			coverPictureState,
			experienceTracker,
			showAIUnsplashHeaderRemovedFlag,
			createAnalyticsEvent,
			regenerationCount,
			updateContentProperty,
			hasCoverPicture,
			setAIHeaderImageStatus,
			propertyKeyForMutations,
			isSmartSpaceOverview,
			refetch,
			analyticsSource,
		]);

		const onImageWidthChange = useCallback(
			async (coverPictureWidth: COVERPICTUREWIDTH) => {
				if (coverPictureWidth !== coverPictureState.state.coverPictureWidth) {
					void coverPictureState.toggleCoverPictureWidthButton(coverPictureWidth);

					//hack the input and outout have different capitalizations TT_TT
					// the mutation returns lowercase, the mutation expects uppercase
					await mutateCoverPictureWidth({
						variables: {
							contentId,
							// Casting to GraphQLCoverPictureWidth as structurally equivalent enums are not equal in TS
							coverPictureWidth: coverPictureWidth.toUpperCase() as GraphQLCoverPictureWidth,
						},
					});
				}
			},
			[contentId, mutateCoverPictureWidth, coverPictureState],
		);

		const onSaveReposition = async (position) => {
			// check that the position isn't the same as the current one
			if (position != coverPicture.current?.draft?.value.position) {
				const version = String((coverPicture.current?.draft?.version || 0) + 1);
				const propertyValue = JSON.stringify({
					id: coverPicture.current?.draft?.value.id,
					position,
				});
				coverPicture.current.draft!.value.position = position;
				coverPicture.current.draft!.version = parseInt(version);
				const updatePromise = updateContentProperty({
					propertyKey: propertyKeyForMutations,
					propertyValue,
					isDelete: false,
					hasExistingProperty: hasCoverPicture,
				});
				if (isSmartSpaceOverview && updatePromise) {
					void updatePromise.then(() => {
						void refetchAfterMutation();
					});
				}
			}
		};

		const openMediaBrowser = () => {
			setIsMediaBrowserOpen(true);
		};

		const closeMediaBrowser = () => {
			setIsMediaBrowserOpen(false);
		};

		const createLiveDocHeaderImageAnalyticsEvent = useCallback(
			(isUnsplashImage: boolean) => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'viewed',
						actionSubject: 'LiveDocHeaderImage',
						actionSubjectId: 'liveDocHeaderImage',
						source: 'liveDocHeaderImage',
						attributes: {
							headerImageType: isUnsplashImage ? 'unsplash' : 'user-uploaded',
							isAIGenerationEnabled: isExperimentEnabled(
								'cc_page_experiences_ai_header_image_generation',
							),
							isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
								'cc_page_experiences_aifc_auto_generated_header_img',
							),
						},
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		useEffect(() => {
			if (hasCoverPicture && isLivePage && isUnsplashLocalCheck != null) {
				createLiveDocHeaderImageAnalyticsEvent(isUnsplashLocalCheck);
			}
		}, [isUnsplashLocalCheck, isLivePage, hasCoverPicture, createLiveDocHeaderImageAnalyticsEvent]);

		return (
			<TwoStageHydration
				name="editor-page-cover-picture"
				// cc_editor_two_stage_hydration_removal - When cleaning up this experiment, also remove the related TwoStageHydration component
				enabled={!expValEquals('cc_editor_two_stage_hydration_removal', 'editorCoverPicture', true)}
				ssrPlaceholder
			>
				<Flex>
					{hasCoverPicture && (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
						<style
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{
								__html: `
									@container editor-area (width < 760px) {
										.cover-picture-wrapper-dangerously-set-inner-html {
											height: ${smartSpaceOverviewHeight ?? 147}px
										}
									}
									@container editor-area (width >= 4071px) {
										.cover-picture-wrapper-dangerously-set-inner-html {
											height: ${smartSpaceOverviewHeight ?? 750}px
										}
									}
									`,
							}}
						/>
					)}
					<CoverPictureWrapper
						hasCoverPicture={hasCoverPicture}
						isUploadingPicture={isUploadingPicture}
						data-testid="cover-picture-wrapper"
						width={width}
						dynamicCoverImageHeight={dynamicCoverImageHeight}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className="cover-picture-wrapper-dangerously-set-inner-html"
						isFixedWidthImageOption={isFixedWidthImageOption}
						smartSpaceOverviewHeight={smartSpaceOverviewHeight}
						isSmartSpaceOverview={isSmartSpaceOverview}
						isReadOnly={isReadOnly}
					>
						{hasCoverPicture &&
							(mediaConfig || hasSSRConfigInSSRModeOrHydration) &&
							(isUnsplashLocalCheck ? (
								<UnsplashCoverPicture
									coverPicture={coverPicture}
									dynamicCoverImageHeight={dynamicCoverImageHeight}
									isFixedWidthImageOption={isFixedWidthImageOption}
									smartSpaceOverviewHeight={smartSpaceOverviewHeight}
									coverPictureDataPosition={coverPictureDataPosition}
									coverPictureDataId={coverPictureDataId}
									isRoundedCorners={isSmartSpaceOverview}
								/>
							) : (
								<MediaCoverPicture
									coverPicture={coverPicture}
									mediaConfig={mediaConfig}
									ssrConfig={ssrConfig}
									contentId={contentId}
									showErrorFlag={showErrorFlag}
									hasSSRConfigInSSRModeOrHydration={hasSSRConfigInSSRModeOrHydration}
									isFixedWidthImageOption={isFixedWidthImageOption}
									smartSpaceOverviewHeight={smartSpaceOverviewHeight}
									isAIGenerated={!!aiHeaderImageFileId}
									isRoundedCorners={isSmartSpaceOverview}
								/>
							))}
						{!isReadOnly && (
							<ImagePicker
								hasCoverPicture={hasCoverPicture}
								openMediaBrowser={openMediaBrowser}
								onRemoveImage={onRemoveImage}
								userOffline={userOffline}
								isUploadingPicture={isUploadingPicture}
								handleUnsplashUpload={handleUnsplashUpload}
								setIsUploadingPicture={setIsUploadingPicture}
								initialPicturePosition={coverPicture.current.draft?.value.position}
								onSaveReposition={onSaveReposition}
								shouldShowReposition={shouldShowReposition}
								setShouldShowReposition={setShouldShowReposition}
								coverPictureState={coverPictureState}
								floatingToolbarButtonStyle={floatingToolbarButtonStyle}
								forceUpdate={forceUpdate}
								contentId={contentId}
								dynamicCoverImageHeight={dynamicCoverImageHeight}
								smartSpaceOverviewHeight={smartSpaceOverviewHeight}
								onImageWidthChange={isSmartSpaceOverview ? undefined : onImageWidthChange}
								isRoundedCorners={isSmartSpaceOverview}
								spaceKey={spaceKey}
								isRegeneratingImage={isRegeneratingImage}
								onRegenerate={onRegenerate}
								onAcceptAIUnsplashImage={onAcceptAIUnsplashImage}
								shouldShowAIUnsplashHeaderToolbar={shouldShowAIUnsplashHeaderToolbar}
								isFixedWidthImageOption={isFixedWidthImageOption}
								generateAIHeaderImage={
									generateAIHeaderImage ? wrappedGenerateAIHeaderImage : undefined
								}
								// AI Image Acceptance Flow props
								shouldShowAIImageAcceptanceFlow={
									coverPictureState.state.shouldShowAIImageAcceptanceFlow
								}
								onAcceptAIGeneratedImage={onAcceptAIGeneratedImage}
								onRetryAIGeneratedImage={onRetryAIGeneratedImage}
								isRetryingAIImage={isRetryingAIImage}
								analyticsSource={analyticsSource}
							/>
						)}
					</CoverPictureWrapper>
					{!isReadOnly && mediaConfig && (
						<Browser
							mediaClientConfig={{
								authProvider: mediaConfig?.authProvider,
								useSha256ForUploads: mediaConfig?.useSha256ForUploads,
							}}
							config={{
								multiple: false,
								fileExtensions: ['.jpg', '.jpeg', '.png', '.gif'],
								uploadParams: {
									collection: mediaConfig.collection,
									onUploadRejection: mediaConfig?.onUploadRejection,
								},
							}}
							isOpen={isMediaBrowserOpen}
							onClose={closeMediaBrowser}
							onUploadsStart={handleUploadStart}
							onError={showErrorFlag}
							onEnd={handleUploadEnd}
						/>
					)}
				</Flex>
			</TwoStageHydration>
		);
	},
);

export const EditorPageCoverPicture = withFlags(
	withTransparentErrorBoundary({
		attribution: Attribution.PAGE_EXTENSIONS,
	})(EditorPageCoverPictureComponent),
);
